import { Injectable } from '@angular/core';
import { Observable, of, throwError, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { RemoveUnderscorePipe } from '../pipes/remove-underscore.pipe';

export interface DocumentItem {
  id: string
  attachableType: string
  formatType: string
  documentCode: string
  originalFilename: string
  description: string
  createdAt: string
}

@Injectable({ providedIn: 'root' })
export class AttachmentService {
  constructor(private httpClient: HttpClient) { }
  private baseUrl = environment.baseUrl;

  removeUnderScore(value: string) { return new RemoveUnderscorePipe().transform(value) }


  uploadBase64(
    payload: {
      attachableType: string;
      filename: string;
      extension: string;
      documentCode?: string;
      base64Content: string;
      referenceNumber?: string;
    }
  ): Observable<any> {
    const url = `${this.baseUrl}public/attachment/save-attachment`;

    return this.httpClient.post<any>(url, payload).pipe(
      catchError(err => throwError(() => err))
    );
  }

  fetchAttachmentById(id: string,): Observable<{ base64?: string; url?: string; fileName?: string; }> {
    const url = `${this.baseUrl}public/attachment/download`;
    const headers = new HttpHeaders({
      applicationId: id,
    });
    return this.httpClient.get<any>(url, { headers });
  }

  deleteAttachment(id: string): Observable<any> {
    const url = `${this.baseUrl}attachments/${id}`;
    return this.httpClient.delete<any>(url);
  }

  downloadAttachment(id: string): Observable<Blob> {
    const url = `${this.baseUrl}attachments/download/${id}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  fetchByIds(ids: string[]) {
    if (!ids?.length) {
      return of([]);
    }
    return forkJoin(
      ids.map(id =>
        this.fetchAttachmentById(id).pipe(
          catchError(() => of(null))
        )
      )
    );
  }

  mapAttachment(data: any) {
    if (!data?.base64Content || !data?.metadata) return null;

    const meta = data.metadata;

    const binary = atob(data.base64Content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const mimeMap: Record<string, string> = {
      PNG: 'image/png',
      JPG: 'image/jpeg',
      JPEG: 'image/jpeg',
      PDF: 'application/pdf',
      GIF: 'image/gif',
      SVG: 'image/svg+xml',
      WEBP: 'image/webp',
      DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      XLS: 'application/vnd.ms-excel'
    };

    const mimeType =
      mimeMap[meta.formatType?.toUpperCase()] || 'application/octet-stream';

    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const filename = meta.originalFilename || this.removeUnderScore(meta.documentCode);
    const title = meta.documentCode ? this.removeUnderScore(meta.documentCode) : filename;
    const description = meta.originalFilename || filename;

    return {
      id: meta.id,
      name: filename,
      title,
      description,
      type: mimeType,
      url,
      size: blob.size
    };
  }

  getDocuments(): Observable<DocumentItem[]> {
    const url = `${this.baseUrl}public/search/legislation`;
    return this.httpClient
      .get<{ status: boolean; message: string; data: DocumentItem[] }>(url)
      .pipe(map(res => res.data));
  }

  downloadLegislation(id: string): Observable<{ metadata: any; base64Content: string; fileSize: number }> {
    const url = `${this.baseUrl}public/search/legislation/download`;
    const headers = new HttpHeaders({ attachmentId: id });
    return this.httpClient
      .get<{ status: boolean; message: string; data: { metadata: any; base64Content: string; fileSize: number } }>(url, { headers })
      .pipe(map(res => res.data));
  }


}
